import { GetterTree, ActionTree, MutationTree, ActionContext } from 'vuex';
import { SortOrder } from '@/mixins/TableMixin';
import { cloneDeep } from 'lodash';

export interface TableStateConfig {
  actionName: string;
  getterName: string;
}

export const tableStateConfig: { [key: string]: TableStateConfig } = {
  contacts: {
    actionName: 'addContactsTableState',
    getterName: 'contactsTableState'
  },
  courses: {
    actionName: 'addCoursesTableState',
    getterName: 'coursesTableState'
  },
  bookings: {
    actionName: 'addBookingsTableState',
    getterName: 'bookingsTableState'
  },
  donations: {
    actionName: 'addDonationsTableState',
    getterName: 'donationsTableState'
  },
  campaignDonations: {
    actionName: 'addCampaignDonationsTableState',
    getterName: 'campaignDonationsTableState'
  },
  attendance: {
    actionName: 'addAttendanceTableState',
    getterName: 'attendanceTableState'
  },
  courseBookings: {
    actionName: 'addCourseBookingsTableState',
    getterName: 'courseBookingsTableState'
  },
  donationHistory: {
    actionName: 'addDonationHistoryTableState',
    getterName: 'donationHistoryTableState'
  },
  purchaseHistory: {
    actionName: 'addPurchaseHistoryTableState',
    getterName: 'purchaseHistoryTableState'
  },
  userManagement: {
    actionName: 'addUserManagementTableState',
    getterName: 'userManagementTableState'
  }
};

interface TableState {
  searchValue: string;
  searchName: string;
  currentPage: number;
  currentSort: string;
  secondarySort?: string;
  currentSortOrder: SortOrder;
  filters: { [key: string]: string } | null;
  plainFilters: { [key: string]: string } | null;
}

interface LoadingState {
  contactsTableState: TableState;
  coursesTableState: TableState;
  bookingsTableState: TableState;
  donationsTableState: TableState;
  campaignDonationsTableState: TableState;
  attendanceTableState: TableState;
  courseBookingsTableState: TableState;
  donationHistoryTableState: TableState;
  purchaseHistoryTableState: TableState;
  userManagementTableState: TableState;
}

export const defaultTablesStateState: LoadingState = {
  contactsTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'first_name',
    secondarySort: 'last_name',
    currentSortOrder: SortOrder.asc,
    filters: null,
    plainFilters: null
  },
  coursesTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'status_id',
    // secondarySort: 'name',
    currentSortOrder: SortOrder.desc,
    filters: null,
    plainFilters: null
  },
  bookingsTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'date',
    // secondarySort: '-date',
    currentSortOrder: SortOrder.desc,
    filters: null,
    plainFilters: null
  },
  donationsTableState: {
    searchValue: '',
    searchName: 'name',
    currentPage: 1,
    currentSort: 'last_donation_date',
    // secondarySort: '-last_donation_date',
    currentSortOrder: SortOrder.desc,
    filters: null,
    plainFilters: null
  },
  campaignDonationsTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'date',
    // secondarySort: 'customer_name',
    currentSortOrder: SortOrder.desc,
    filters: null,
    plainFilters: null
  },
  attendanceTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'customers.first_name',
    // secondarySort: '-date',
    currentSortOrder: SortOrder.asc,
    filters: null,
    plainFilters: null
  },
  courseBookingsTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'event.status_id',
    secondarySort: '-date',
    currentSortOrder: SortOrder.asc,
    filters: null,
    plainFilters: null
  },
  donationHistoryTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'date',
    // secondarySort: 'customer_name',
    currentSortOrder: SortOrder.desc,
    filters: null,
    plainFilters: null
  },
  purchaseHistoryTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'status',
    // secondarySort: 'external_id',
    currentSortOrder: SortOrder.asc,
    filters: null,
    plainFilters: null
  },
  userManagementTableState: {
    searchValue: '',
    searchName: 'search',
    currentPage: 1,
    currentSort: 'first_name',
    secondarySort: 'last_name',
    currentSortOrder: SortOrder.asc,
    filters: null,
    plainFilters: null
  }
};

const state: LoadingState = cloneDeep(defaultTablesStateState);

const mutations: MutationTree<LoadingState> = {
  ADD_CONTACTS_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.contactsTableState = payload;
  },
  ADD_COURSES_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.coursesTableState = payload;
  },
  ADD_BOOKINGS_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.bookingsTableState = payload;
  },
  ADD_DONATIONS_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.donationsTableState = payload;
  },
  ADD_CAMPAIGN_DONATIONS_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.campaignDonationsTableState = payload;
  },
  ADD_ATTENDANCE_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.attendanceTableState = payload;
  },
  ADD_COURSE_BOOKINGS_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.courseBookingsTableState = payload;
  },
  ADD_DONATION_HISTORY_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.donationHistoryTableState = payload;
  },
  ADD_PURCHASE_HISTORY_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.purchaseHistoryTableState = payload;
  },
  ADD_USER_MANAGEMENT_TABLE_STATE(returnedState: LoadingState, payload: TableState): void {
    returnedState.userManagementTableState = payload;
  }
};

const actions: ActionTree<LoadingState, {}> = {
  addContactsTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_CONTACTS_TABLE_STATE', payload);
  },
  addCoursesTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_COURSES_TABLE_STATE', payload);
  },
  addBookingsTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_BOOKINGS_TABLE_STATE', payload);
  },
  addDonationsTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_DONATIONS_TABLE_STATE', payload);
  },
  addCampaignDonationsTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_CAMPAIGN_DONATIONS_TABLE_STATE', payload);
  },
  addAttendanceTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_ATTENDANCE_TABLE_STATE', payload);
  },
  addCourseBookingsTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_COURSE_BOOKINGS_TABLE_STATE', payload);
  },
  addDonationHistoryTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_DONATION_HISTORY_TABLE_STATE', payload);
  },
  addPurchaseHistoryTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_PURCHASE_HISTORY_TABLE_STATE', payload);
  },
  addUserManagementTableState: ({ commit }: ActionContext<LoadingState, {}>, payload: TableState): void => {
    commit('ADD_USER_MANAGEMENT_TABLE_STATE', payload);
  }
};

const getters: GetterTree<LoadingState, {}> = {
  contactsTableState: (returnedState: LoadingState): TableState => {
    return returnedState.contactsTableState;
  },
  coursesTableState: (returnedState: LoadingState): TableState => {
    return returnedState.coursesTableState;
  },
  bookingsTableState: (returnedState: LoadingState): TableState => {
    return returnedState.bookingsTableState;
  },
  donationsTableState: (returnedState: LoadingState): TableState => {
    return returnedState.donationsTableState;
  },
  campaignDonationsTableState: (returnedState: LoadingState): TableState => {
    return returnedState.campaignDonationsTableState;
  },
  attendanceTableState: (returnedState: LoadingState): TableState => {
    return returnedState.attendanceTableState;
  },
  courseBookingsTableState: (returnedState: LoadingState): TableState => {
    return returnedState.courseBookingsTableState;
  },
  donationHistoryTableState: (returnedState: LoadingState): TableState => {
    return returnedState.donationHistoryTableState;
  },
  purchaseHistoryTableState: (returnedState: LoadingState): TableState => {
    return returnedState.purchaseHistoryTableState;
  },
  userManagementTableState: (returnedState: LoadingState): TableState => {
    return returnedState.userManagementTableState;
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};
